// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps, Typography } from '@mui/material';

interface IOverflowTooltipTextProps {
  text: string;
  sx: object;
  // Dense lists point the tooltip sideways, add a dwell delay, and push it
  // clear of trailing row controls so it never buries neighbouring rows
  // (AUT-6409 tile rows). popperOffset is popper's [skidding, distance].
  placement?: TooltipProps['placement'];
  enterDelay?: number;
  popperOffset?: [number, number];
}

// Single-line text that ellipsizes when it outgrows its box, and then - and only then -
// offers the full value in a hover tooltip. Text that fits gets no tooltip at all (an empty
// title disables MUI's Tooltip). Truncation is measured on the rendered node and re-measured
// when the box resizes, so the answer tracks the responsive card widths.
export const OverflowTooltipText = ({
  text, sx, placement, enterDelay, popperOffset,
}: IOverflowTooltipTextProps) => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;
    const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth);
    measure();
    // jsdom has no ResizeObserver; there, the initial measure (0 > 0, never truncated) stands.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={isTruncated ? text : ''}
      arrow
      placement={placement}
      enterDelay={enterDelay}
      enterNextDelay={enterDelay}
      PopperProps={popperOffset && {
        modifiers: [{ name: 'offset', options: { offset: popperOffset } }],
      }}
      componentsProps={{
        tooltip: {
          // Filenames and machine names have no spaces; without this the
          // tooltip only wraps at hyphens and turns into odd shapes.
          sx: { maxWidth: 240, overflowWrap: 'anywhere' },
        },
      }}
    >
      <Typography ref={textRef} noWrap sx={sx}>
        {text}
      </Typography>
    </Tooltip>
  );
};

OverflowTooltipText.defaultProps = {
  placement: 'top',
  enterDelay: 100,
  popperOffset: undefined,
};

export default OverflowTooltipText;
